import {
    Box,
    IconButton,
    InputAdornment,
    LinearProgress,
    Popover,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ASSISTANT_MODES } from '../../constants';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChatsTabBody from './components/ChatsTabBody';
import CloseIcon from '@mui/icons-material/Close';
import Locale from '/b2b/common/components/Locale';
import { PANEL_MODES } from '../../state/reducer';
import PersonIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import makeStyles from '@mui/styles/makeStyles';
import styles from './ChatsPopover.styles';
import { translate } from '/b2b/common/helpers/i18n';
import useAIAssistant from '../../useAIAssistant';

const useStyles = makeStyles(styles);

/*
 * Rows per request, for the hydrate and every "load more" alike — chat-api pages by
 * offset, so a later page asked for at a different size would overlap the one before
 * it. 100 is chat-api's own ceiling.
 */
const PAGE_SIZE = 100;

/*
 * The conversation browser: AI / Privacy Team tabs, search, paginated lists, and
 * the per-row delete affordance. Owns its own view state (tab, search, pagination);
 * selection and deletion delegate to the shell via callbacks.
 */
const ChatsPopover = props => {
    const {
        open,
        anchorEl,
        onClose,
        activeConversationId,
        onSelectConversation,
        onDeleteConversation,
    } = props;
    // Note: panelMode is read from props by the styles (max-height fits the pop-up).
    const classes = useStyles(props);
    const {
        conversations,
        isLoadingConversations,
        loadConversations,
        activeConversation,
        isChatApiEnabled,
    } = useAIAssistant();

    const [activeTab, setActiveTab] = useState(ASSISTANT_MODES.AI);
    const [search, setSearch] = useState('');
    const [searchVisible, setSearchVisible] = useState(false);
    const [pagination, setPagination] = useState({
        ai: { page: 1, hasNext: false },
        human: { page: 1, hasNext: false },
    });

    /*
     * Which tab a conversation belongs to. chat-api keeps one list and marks the threads
     * the Privacy Team owns with an escalation, so that field is the split; the legacy
     * backend keeps two lists, told apart by mode.
     */
    const isPrivacyTeamConversation = isChatApiEnabled
        ? c => Boolean(c.escalation)
        : c => c.mode === ASSISTANT_MODES.HUMAN;

    /*
     * Hydrate the lists up front so switching tabs is instant. chat-api answers both
     * tabs from a single request, so ask once and share the page state; the legacy
     * backend needs one call per mode.
     */
    useEffect(() => {
        setPagination({
            ai: { page: 1, hasNext: false },
            human: { page: 1, hasNext: false },
        });
        const load = async () => {
            if (isChatApiEnabled) {
                const result = await loadConversations({ pageSize: PAGE_SIZE });
                const page = { page: 1, hasNext: result?.hasNextPage || false };
                setPagination({ ai: page, human: page });
                return;
            }
            const [aiResult, humanResult] = await Promise.all([
                loadConversations({ mode: ASSISTANT_MODES.AI, pageSize: PAGE_SIZE }),
                loadConversations({ mode: ASSISTANT_MODES.HUMAN, pageSize: PAGE_SIZE }),
            ]);
            setPagination({
                ai: { page: 1, hasNext: aiResult?.hasNextPage || false },
                human: { page: 1, hasNext: humanResult?.hasNextPage || false },
            });
        };
        load();
    }, [loadConversations, isChatApiEnabled]);

    /*
     * Snap the tab to where the active conversation lives so the popover opens on the
     * relevant list. AI is the default (covers ai-mode and a brand-new chat).
     */
    const isActivePrivacyTeam = activeConversation
        ? isPrivacyTeamConversation(activeConversation)
        : false;
    useEffect(() => {
        setActiveTab(isActivePrivacyTeam ? ASSISTANT_MODES.HUMAN : ASSISTANT_MODES.AI);
    }, [isActivePrivacyTeam]);

    // Reset to the hidden default whenever the popover closes.
    useEffect(() => {
        if (!open) {
            setSearch('');
            setSearchVisible(false);
        }
    }, [open]);

    const searchTerm = search.trim().toLowerCase();
    const matchesSearch = c => !searchTerm || c.title?.toLowerCase().includes(searchTerm);
    /*
     * AI is the default; optimistic conversations carrying neither tag are treated as
     * AI so they don't accidentally hide from the user.
     */
    const aiConversations = conversations
        .filter(c => !isPrivacyTeamConversation(c))
        .filter(matchesSearch);
    const humanConversations = conversations
        .filter(isPrivacyTeamConversation)
        .filter(matchesSearch);
    const isAiTab = activeTab === ASSISTANT_MODES.AI;

    const handleTabChange = (_event, value) => {
        setActiveTab(value);
        setSearch('');
    };

    const toggleSearch = () => {
        if (searchVisible) setSearch('');
        setSearchVisible(!searchVisible);
    };

    const handleLoadMore = async tab => {
        const nextPage = pagination[tab].page + 1;
        const result = await loadConversations({
            // chat-api pages one list; the mode would be meaningless to it.
            ...(isChatApiEnabled ? {} : { mode: tab }),
            page: nextPage,
            // Must match the hydrate's size: chat-api pages by offset, so asking for
            // page 2 at the default size would re-serve rows the first page already
            // holds and the click would appear to do nothing.
            pageSize: PAGE_SIZE,
            silent: true,
        });
        const next = { page: nextPage, hasNext: result?.hasNextPage || false };
        setPagination(prev =>
            isChatApiEnabled ? { ai: next, human: next } : { ...prev, [tab]: next }
        );
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            classes={{ paper: classes.popoverPaper }}
        >
            <Box className={classes.popoverHeader}>
                <Typography variant="subtitle1" className={classes.popoverTitle}>
                    <Locale path="aiAssistant.buttons.chats.label" />
                </Typography>
                <Box className={classes.popoverHeaderActions}>
                    <IconButton
                        size="small"
                        onClick={toggleSearch}
                        aria-label={translate('aiAssistant.inputs.searchChats.placeholder')}
                        aria-pressed={searchVisible}
                        sx={{ color: searchVisible ? 'primary.main' : undefined }}
                    >
                        <SearchIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label={translate('common.close')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                className={classes.popoverTabs}
            >
                <Tab
                    value={ASSISTANT_MODES.AI}
                    icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                    iconPosition="start"
                    label={translate('aiAssistant.name')}
                    className={classes.popoverTab}
                />
                <Tab
                    value={ASSISTANT_MODES.HUMAN}
                    icon={<PersonIcon fontSize="small" />}
                    iconPosition="start"
                    label={translate('aiAssistant.sender.privacyTeam')}
                    className={classes.popoverTab}
                />
            </Tabs>
            {searchVisible && (
                <Box className={classes.popoverSearch}>
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        placeholder={translate('aiAssistant.inputs.searchChats.placeholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            autoComplete: 'off',
                            'aria-label': translate('aiAssistant.inputs.searchChats.placeholder'),
                        }}
                    />
                </Box>
            )}
            {isLoadingConversations && <LinearProgress />}
            <ChatsTabBody
                classes={classes}
                conversations={isAiTab ? aiConversations : humanConversations}
                isLoading={isLoadingConversations}
                hasNextPage={pagination[activeTab].hasNext && !searchTerm}
                activeConversationId={activeConversationId}
                emptyMessageKey={
                    isAiTab ? 'aiAssistant.tabs.ai.empty' : 'aiAssistant.tabs.privacyTeam.empty'
                }
                showDelete={isAiTab}
                onSelect={onSelectConversation}
                onDelete={isAiTab ? onDeleteConversation : undefined}
                onLoadMore={() => handleLoadMore(activeTab)}
            />
        </Popover>
    );
};

ChatsPopover.propTypes = {
    open: PropTypes.bool,
    anchorEl: PropTypes.any,
    onClose: PropTypes.func.isRequired,
    activeConversationId: PropTypes.string,
    onSelectConversation: PropTypes.func.isRequired,
    onDeleteConversation: PropTypes.func.isRequired,
    panelMode: PropTypes.oneOf(Object.values(PANEL_MODES)),
};

export default ChatsPopover;
